import styles from "./SubscriptionPopup.module.scss";
import React, { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import CustomButton from "../../../../components/ui/CustomButton/CustomButton";
import { Form, FormInstance } from "antd";
import FormItemInputNumber from "../../../../components/ui/FormItemInputNumber/FormItemInputNumber";
import euroSvg from "../../../../assets/images/icons/euro.svg";
import FormItemTextArea from "../../../../components/ui/FormItemTextArea/FormItemTextArea";
import {
  ISubscription,
  ISubscriptionUpdateModel,
  SubscriptionDurationTypesEnum,
} from "../../../../models/Subscription";
import { SubscriptionService } from "../../../../services/subscription.service";
import useAppDispatch from "../../../../hooks/useAppDispatch";
import { openSuccessMessage } from "../../../../store/message/messageSlice";
import { useNavigate } from "react-router-dom";
import FormItemInput from "../../../../components/ui/FormItemInput/FormItemInput";
import FormItemSelect from "../../../../components/ui/FormItemSelect/FormItemSelect";
import FormItemCheckbox from "../../../../components/ui/FormItemCheckbox/FormItemCheckbox";
import { handleError } from "../../../../helper/errorHandler";
import { useSubscriptionMutations } from "../../../../api/mutations";

interface ISubscriptionPopupProps {
  setShowCreatePopup: (value: boolean) => void;
  item: ISubscription | null;
}

const SubscriptionPopup: React.FC<ISubscriptionPopupProps> = ({ setShowCreatePopup, item }) => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const formRef = useRef<FormInstance>(null);
  const navigate = useNavigate();

  const { createSubscription, updateSubscription } = useSubscriptionMutations();

  const durationTypesSelectItems = [
    {
      id: SubscriptionDurationTypesEnum.day,
      label: t(
        SubscriptionDurationTypesEnum.day
          .toUpperCase()
          .slice(0, SubscriptionDurationTypesEnum.day.length - 1) + "ILY"
      ),
    },
    {
      id: SubscriptionDurationTypesEnum.week,
      label: t(SubscriptionDurationTypesEnum.week.toUpperCase() + "LY"),
    },
    {
      id: SubscriptionDurationTypesEnum.month,
      label: t(SubscriptionDurationTypesEnum.month.toUpperCase() + "LY"),
    },
    {
      id: SubscriptionDurationTypesEnum.year,
      label: t(SubscriptionDurationTypesEnum.year.toUpperCase() + "LY"),
    },
  ];

  useEffect(() => {
    if (formRef.current) {
      if (item) {
        formRef.current.setFieldsValue({
          name: item.name,
          duration: item.duration,
          durationType: item.durationType,
          isVisible: !!item.isVisible,
          price: item.price,
          credits: item.credits,
          description: item.description,
          creditsRolloverAmount: item.creditsRolloverAmount,
        });
      } else {
        formRef.current.setFieldsValue({
          durationType: SubscriptionDurationTypesEnum.month,
          isVisible: true,
        });
      }
    }
  }, [item, formRef.current]);

  const onFinish = async (values: Omit<ISubscription, "id">) => {
    if (item) {
      const modelForUpdate: ISubscriptionUpdateModel = {
        name: values.name,
        isVisible: values.isVisible,
        credits: values.credits,
        description: values.description,
        creditsRolloverAmount: values.creditsRolloverAmount,
      };
      updateSubscription(
        { id: item.id, model: modelForUpdate },
        {
          onSuccess: () => {
            dispatch(openSuccessMessage(t("SubscriptionUpdatedSuccessfully")));
            setShowCreatePopup(false);
            redirect();
          },
          onError: (e) => handleError(e, dispatch, t),
        }
      );
    } else {
      createSubscription(
        { ...values },
        {
          onSuccess: () => {
            dispatch(openSuccessMessage(t("SubscriptionCreatedSuccessfully")));
            setShowCreatePopup(false);
            redirect();
          },
          onError: (e) => handleError(e, dispatch, t),
        }
      );
    }
  };

  const redirect = () => {
    navigate(`/subscriptions/?shouldUpdateSubscriptions=${true}`);
  };

  const clickOnClose = () => {
    setShowCreatePopup(false);
  };

  return (
    <div className={styles.container}>
      <div className={styles.popup}>
        <h3 className={styles.title}>{item ? t("UpdateSubscription") : t("AddNewSubscription")}</h3>
        <Form
          className={styles.form}
          ref={formRef}
          name="create-subscription"
          onFinish={onFinish}
          autoComplete="off"
          layout={"vertical"}
        >
          <FormItemInput name={"name"} label={t("Name") + " *"} rules={{ required: true }} />

          <div className={styles.row}>
            <FormItemSelect
              name={"durationType"}
              label={t("DurationType") + " *"}
              rules={{ required: true }}
              disabled={!!item}
              items={durationTypesSelectItems}
            />
            <FormItemInput
              name={"duration"}
              label={t("Duration") + " *"}
              rules={{ required: true }}
              disabled={!!item}
            />
          </div>

          <div className={styles.row}>
            <FormItemInputNumber
              name={"credits"}
              label={t("NumberOfCredits") + " *"}
              rules={{ required: true }}
            />
            <FormItemInputNumber
              prefix={euroSvg}
              name={"price"}
              label={t("Price") + " *"}
              rules={{ required: true }}
              disabled={!!item}
            />
          </div>

          <FormItemInputNumber
            name={"creditsRolloverAmount"}
            label={t("CreditsRolloverAmount") + " *"}
            rules={{ required: true }}
          />

          <FormItemTextArea
            maxLength={255}
            name={"description"}
            label={t("Description") + " *"}
            rules={{ required: true }}
          />

          <div className={styles.row}>
            <FormItemCheckbox
              name={"isVisible"}
              label={t("VisibleForUser")}
              rules={{ required: false }}
            />
          </div>

          <div className={styles.btnsWrapper}>
            <CustomButton
              text={t("Close")}
              type={"button"}
              typeStyle={"outline"}
              functionOnClick={clickOnClose}
            />
            <CustomButton text={t("Save")} type={"submit"} typeStyle={"fill"} />
          </div>
        </Form>
      </div>
    </div>
  );
};

export default SubscriptionPopup;
